/**
 * Client-side record of which Issa chat action buttons have already been acted on.
 *
 * The chat log itself is server-side and immutable — re-opening a conversation
 * re-renders the same `<button>` block, so without a local record the card would
 * offer "Create Deal" again for a deal that already exists. This keeps a small
 * localStorage map so the card can show (and link to) what it created, across
 * reloads and across tabs.
 *
 * Deliberately best-effort: every access is guarded, because localStorage throws
 * in private-mode/blocked-cookie contexts, and a missing record only means the
 * card falls back to offering the action again.
 */

const STORAGE_KEY = "issaChatDealActions";
/** Keep the map small — oldest entries are pruned first. */
const MAX_ENTRIES = 300;

/** What a chat action button did. `kind` says which action it was. */
export interface RecordedDealAction {
  /** Package deal id for deal actions; the product id for a single-product price
   *  change; 0 for a bulk price change (see `count`); the source kiosk id for a
   *  planogram (the new planogram's id is not returned by the save). */
  dealId: number;
  /** ISO timestamp of when it happened, for pruning + display. */
  at: string;
  kind: "create" | "update" | "prices" | "planogram";
  /** Number of records affected — set for bulk actions such as a price change, or
   *  the number of slots a created planogram changed. */
  count?: number;
}

type ActionMap = Record<string, RecordedDealAction>;

function read(): ActionMap {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? (parsed as ActionMap) : {};
  } catch {
    return {};
  }
}

function write(map: ActionMap): void {
  try {
    const keys = Object.keys(map);
    if (keys.length > MAX_ENTRIES) {
      // Prune oldest by timestamp.
      const sorted = keys.sort((a, b) => (map[a]!.at < map[b]!.at ? -1 : 1));
      for (const k of sorted.slice(0, keys.length - MAX_ENTRIES)) delete map[k];
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* storage unavailable — the in-memory state still covers this session */
  }
}

/** Stable non-cryptographic hash (djb2) — identifies an action by its content. */
function hashString(s: string): string {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

/**
 * Identity for one action button. Uses the chat log row id + position when the
 * message is persisted server-side (precise: the same suggestion re-opened later
 * matches, a genuinely new suggestion does not), falling back to a content hash
 * when there is no log id to key on.
 */
export function actionKey(messageId: number | undefined, index: number, action: unknown): string {
  const content = hashString(JSON.stringify(action ?? {}));
  return messageId && messageId > 0 ? `m${messageId}:${index}:${content}` : `c:${content}`;
}

export function getRecordedAction(key: string): RecordedDealAction | null {
  return read()[key] ?? null;
}

export function recordAction(
  key: string,
  dealId: number,
  kind: RecordedDealAction["kind"],
  count?: number,
): RecordedDealAction {
  const entry: RecordedDealAction = { dealId, at: new Date().toISOString(), kind };
  if (count != null) entry.count = count;
  const map = read();
  map[key] = entry;
  write(map);
  return entry;
}
